import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import type { Product } from "@acs/shared";
import { api } from "../api";

export function Sidebar({
  products,
  onChanged,
  onLogout,
}: {
  products: Product[];
  onChanged: () => Promise<void> | void;
  onLogout?: () => void;
}) {
  const [showNew, setShowNew] = useState(false);
  const navigate = useNavigate();

  return (
    <aside className="w-60 border-r bg-white flex flex-col">
      <div className="px-4 py-4 border-b">
        <div className="font-bold text-zinc-900">AI Creative</div>
        <div className="text-xs text-zinc-500 mt-0.5">Station</div>
      </div>
      <div className="px-3 py-3 flex items-center justify-between">
        <span className="text-xs uppercase font-semibold text-zinc-400 tracking-wide">Ürünler</span>
        <button
          onClick={() => setShowNew(true)}
          className="text-xs text-accent hover:underline"
        >
          + Yeni
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto px-2">
        {products.length === 0 ? (
          <div className="px-3 py-2 text-xs text-zinc-400">Henüz ürün yok</div>
        ) : (
          products.map((p) => (
            <NavLink
              key={p.id}
              to={`/products/${p.id}/new`}
              className={({ isActive }) =>
                `block px-3 py-2 text-sm rounded ${
                  isActive ? "bg-zinc-100 text-zinc-900 font-medium" : "text-zinc-700 hover:bg-zinc-50"
                }`
              }
            >
              <div>{p.name}</div>
              <div className="text-xs text-zinc-400">{p.tag}</div>
            </NavLink>
          ))
        )}
      </nav>
      <div className="border-t px-2 py-2 space-y-1">
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `block px-3 py-2 text-sm rounded ${
              isActive ? "bg-zinc-100 text-zinc-900 font-medium" : "text-zinc-600 hover:bg-zinc-50"
            }`
          }
        >
          ⚙ Settings
        </NavLink>
        {onLogout && (
          <button
            onClick={onLogout}
            className="block w-full text-left px-3 py-2 text-sm rounded text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700"
          >
            ↩ Çıkış Yap
          </button>
        )}
      </div>
      {showNew && (
        <NewProductModal
          onClose={() => setShowNew(false)}
          onSaved={async (newId) => {
            setShowNew(false);
            await onChanged();
            // Yeni ürün için önce Ürün Ayarları'na gönder — termin + type tanımlanmadan üretim çalışmaz.
            navigate(`/products/${newId}/settings`);
          }}
        />
      )}
    </aside>
  );
}

function extractDriveFolderId(input: string): string {
  // Accept either bare id or a full Drive URL
  const trimmed = input.trim();
  const match =
    trimmed.match(/\/folders\/([a-zA-Z0-9_-]{20,})/) ??
    trimmed.match(/[?&]id=([a-zA-Z0-9_-]{20,})/);
  return match ? match[1]! : trimmed;
}

function NewProductModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (newId: string) => void | Promise<void>;
}) {
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [folder, setFolder] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const folderId = extractDriveFolderId(folder);
      const res = await api.createProduct({ name, tag, drive_folder_id: folderId });
      await onSaved(res.id);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-lg w-[28rem] max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-lg font-bold mb-4">Yeni Ürün</h2>
        <div className="space-y-3">
          <Field label="Ürün Adı" value={name} onChange={setName} placeholder="BrainUPIQ" />
          <Field
            label="Tag (unique)"
            value={tag}
            onChange={setTag}
            placeholder="biq"
            help="Dosya isimlendirmesinde kullanılır. Kısa, küçük harf, rakam/tire OK. Örn: biq, mlln."
          />
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-zinc-600">Drive Klasör</span>
              <button
                type="button"
                className="text-xs text-accent hover:underline"
                onClick={() => setShowHelp((v) => !v)}
              >
                {showHelp ? "yardımı kapat" : "nereden bulurum?"}
              </button>
            </div>
            <input
              className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              value={folder}
              placeholder="https://drive.google.com/drive/folders/... veya doğrudan ID"
              onChange={(e) => setFolder(e.target.value)}
            />
            <div className="text-xs text-zinc-500 mt-1">
              Tam URL'i yapıştırabilirsin — ID otomatik çıkarılır.
              {folder && extractDriveFolderId(folder) !== folder.trim() && (
                <span className="block text-emerald-600 mt-0.5">
                  ↳ algılanan ID:{" "}
                  <span className="font-mono">{extractDriveFolderId(folder)}</span>
                </span>
              )}
            </div>
            {showHelp && <DriveHelp />}
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="px-3 py-1.5 text-sm rounded border" onClick={onClose}>
            İptal
          </button>
          <button
            className="px-3 py-1.5 text-sm rounded bg-accent text-white disabled:opacity-50"
            disabled={busy || !name || !tag || !folder}
            onClick={submit}
          >
            Oluştur
          </button>
        </div>
      </div>
    </div>
  );
}

function DriveHelp() {
  return (
    <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-3 text-xs text-zinc-700 space-y-2">
      <div className="font-semibold text-zinc-900">Drive klasör ID nasıl bulunur?</div>
      <ol className="list-decimal list-inside space-y-1">
        <li>
          <a
            href="https://drive.google.com/"
            target="_blank"
            rel="noreferrer"
            className="text-accent underline"
          >
            drive.google.com
          </a>
          'a git, ürün için klasör aç (yoksa <span className="font-mono">+ Yeni → Klasör</span>).
        </li>
        <li>Klasörü çift tıklayarak <b>içine gir</b>.</li>
        <li>
          Tarayıcının URL çubuğundaki adres şu formattadır:
          <div className="font-mono bg-white border rounded px-2 py-1 mt-1 break-all">
            drive.google.com/drive/folders/<span className="text-emerald-700">19i_gmt…0Hzwo</span>
          </div>
          Yeşil olan kısım <b>klasör ID</b>'sidir.
        </li>
        <li>
          Tüm URL'i kopyala-yapıştır da yapabilirsin — sistem otomatik çıkarır.
        </li>
      </ol>
      <div className="pt-1 border-t border-amber-200">
        <span className="font-semibold">Önemli:</span> Sistem bu klasöre yazabilmek için{" "}
        <span className="font-mono">akselyamanel@teknasyon.com</span>'a otomatik editor yetkisi verir.
        Senin Drive'ında olmalı (Shared Drive da olur).
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  help,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  help?: string;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-zinc-600 mb-1">{label}</span>
      <input
        className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {help && <span className="block text-xs text-zinc-500 mt-1">{help}</span>}
    </label>
  );
}
